import { useLingui } from '@lingui/react/macro'
import { Box } from 'designSystem/components/Box'
import { Button } from 'designSystem/components/Button'
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
} from 'designSystem/components/Combobox'
import { Field } from 'designSystem/components/Field'
import { Input } from 'designSystem/components/Input'
import { Item, ItemActions, ItemContent } from 'designSystem/components/Item'
import { useOptionalSidePanelPresentation } from 'designSystem/components/sidePanelContext'
import { Text } from 'designSystem/components/Text'
import type { DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import { useCitationLookup } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/search/CitedResponseSegments'
import { ResponseSegmentCitations } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/search/ResponseSegmentCitations'
import {
  type DocChatDraftCitation,
  type DocChatResponseCitations,
  docChatResultPages,
  draftCitationKey,
  draftCitationsToCitations,
  parseDocChatDocumentInput,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/buildDocChatEvalCase'
import { transformSystemDocumentToTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/helpers'
import { buildCitationDocumentLookup } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/resolveCitationDocument'
import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import { useSystemDocumentsByBqIds } from 'features/knowledgeDocumentViewer/hooks/useSystemDocumentsByBqIds'
import { unscopeSearchDocumentIdForHydration } from 'features/knowledgeDocumentViewer/utils/pipelineConfigScopedIds'
import { Check, ChevronDown, ChevronUp, Plus, X } from 'lucide-react'
import React, { type KeyboardEvent, useMemo, useState } from 'react'
import styled from 'styled-components'

import type { PreviewDocumentHandler } from './previewDocumentHandler'

const CitationList = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem 0.5rem;
`

const CitationChip = styled.div`
  display: inline-flex;
  align-items: center;
  min-width: 0;
`

const AddPageRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 5.5rem auto;
  align-items: center;
  gap: 0.5rem;

  [data-slot='combobox-trigger'] {
    width: 100%;
    min-width: 0;
  }

  [data-slot='combobox-value'] {
    display: block;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

const ResultList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const PageButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
`

function documentTitle(row: DocumentTableRow | undefined): string | undefined {
  return row ? row.documentTitle || row.title || row.filename : undefined
}

/**
 * Cited pages on the draft case. Pages come from the reply, a document and
 * page number the curator picks, or the reply's search results.
 */
export function DocChatEvalCaseCitations({
  citations,
  response,
  disabled,
  onAdd,
  onRemove,
  onPreviewDocument,
}: {
  citations: DocChatDraftCitation[]
  response?: DocChatResponseCitations
  disabled: boolean
  onAdd: (citation: DocChatDraftCitation) => void
  onRemove: (citation: DocChatDraftCitation) => void
  onPreviewDocument?: PreviewDocumentHandler
}): React.ReactElement {
  const { t } = useLingui()
  const sidePanel = useOptionalSidePanelPresentation()
  const { selectedPipelineConfigId } = usePipelineConfig()
  const searchLookup = useCitationLookup()
  const [pickedDocumentId, setPickedDocumentId] = useState<string | null>(null)
  const [pageText, setPageText] = useState('')
  const [documentSearch, setDocumentSearch] = useState('')
  const [resultsOpen, setResultsOpen] = useState(false)

  const pasted = useMemo(
    () => parseDocChatDocumentInput(documentSearch, selectedPipelineConfigId),
    [documentSearch, selectedPipelineConfigId]
  )
  const pastedDocumentId =
    pasted?.kind === 'document' ? pasted.documentId : null

  const resultPages = useMemo(
    () => docChatResultPages(response, selectedPipelineConfigId),
    [response, selectedPipelineConfigId]
  )
  const addedKeys = useMemo(
    () =>
      new Set(
        citations.map((citation) =>
          draftCitationKey(citation, selectedPipelineConfigId)
        )
      ),
    [citations, selectedPipelineConfigId]
  )
  const documentIds = useMemo(() => {
    const ids = new Set<string>()
    if (pickedDocumentId) ids.add(pickedDocumentId)
    for (const citation of citations) {
      ids.add(
        unscopeSearchDocumentIdForHydration(
          citation.documentId,
          selectedPipelineConfigId
        )
      )
    }
    for (const result of resultPages) ids.add(result.documentId)
    return [...ids]
  }, [citations, pickedDocumentId, resultPages, selectedPipelineConfigId])

  const missingIds = useMemo(
    () =>
      [...documentIds, ...(pastedDocumentId ? [pastedDocumentId] : [])].filter(
        (id) => !searchLookup.has(id)
      ),
    [documentIds, pastedDocumentId, searchLookup]
  )
  const { documents: fetchedDocuments, isLoading: isLoadingDocuments } =
    useSystemDocumentsByBqIds(missingIds)
  const citationLookup = useMemo(() => {
    if (!fetchedDocuments?.length) return searchLookup
    const lookup = new Map(searchLookup)
    const fetched = buildCitationDocumentLookup(
      fetchedDocuments.map((document) =>
        transformSystemDocumentToTableRow(document)
      )
    )
    for (const [id, row] of fetched) {
      if (!lookup.has(id)) lookup.set(id, row)
    }
    return lookup
  }, [fetchedDocuments, searchLookup])

  const titleFor = (documentId: string) =>
    documentTitle(citationLookup.get(documentId)) ?? documentId

  const isAdded = (documentId: string, pageNum: number) =>
    addedKeys.has(
      draftCitationKey({ documentId, pageNum }, selectedPipelineConfigId)
    )

  const addPage = (documentId: string, pageNum: number) =>
    onAdd({ documentId, pageNum, quotes: [] })

  const documentId = pickedDocumentId ?? ''
  const numPages = documentId
    ? (citationLookup.get(documentId)?.numPages ?? null)
    : null
  const pickedPage = /^\d+$/.test(pageText.trim()) ? Number(pageText) : 0
  const pageInRange =
    pickedPage >= 1 && (numPages === null || pickedPage <= numPages)
  let pageError: string | null = null
  if (pageText.trim() && !pageInRange) {
    pageError =
      numPages === null
        ? t`Enter a page number of 1 or more.`
        : t`Enter a page from 1 to ${numPages}.`
  }
  const pickedAdded =
    documentId !== '' && pageInRange && isAdded(documentId, pickedPage)
  const canAddPicked =
    !disabled && documentId !== '' && pageInRange && !pickedAdded

  const addPicked = () => {
    if (!canAddPicked) return
    addPage(documentId, pickedPage)
    setPickedDocumentId(null)
    setPageText('')
  }

  const addPickedOnEnter = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return
    event.preventDefault()
    addPicked()
  }

  const pickDocument = (nextId: string) => {
    const linkedPage =
      pasted?.kind === 'document' && pasted.documentId === nextId
        ? pasted.pageNum
        : undefined
    setPickedDocumentId(nextId)
    setDocumentSearch('')
    setPageText(linkedPage === undefined ? '' : String(linkedPage))
  }

  const pastedFound =
    pastedDocumentId !== null && citationLookup.has(pastedDocumentId)
  const pickerIds = [
    ...new Set(
      [pickedDocumentId, pastedFound ? pastedDocumentId : null].filter(
        (id): id is string => Boolean(id)
      )
    ),
  ]
  let pasteMessage: string | null = null
  if (pasted?.kind === 'entry_id') {
    pasteMessage = t`Use a DOC-… document id, or a link to a document opened from search.`
  } else if (pasted?.kind === 'no_document') {
    pasteMessage = t`This link does not open a document.`
  } else if (pastedDocumentId && !pastedFound) {
    pasteMessage = isLoadingDocuments
      ? t`Looking up this document…`
      : t`No document with this id on this site.`
  }

  const openResult = (resultDocumentId: string, pageNum: number) => {
    const row = citationLookup.get(resultDocumentId)
    if (!row || !onPreviewDocument) return
    sidePanel?.keepOpenOnNextOutsideClick()
    onPreviewDocument(row, pageNum)
  }

  return (
    <Field>
      <Text as='h4' variant='label'>
        {t`Cited documents`}
      </Text>
      {citations.length ? (
        <>
          <CitationList>
            {citations.map((citation) => (
              <CitationChip
                key={draftCitationKey(citation, selectedPipelineConfigId)}>
                <ResponseSegmentCitations
                  citations={draftCitationsToCitations([citation])}
                  citationLookup={citationLookup}
                  onPreviewDocument={onPreviewDocument}
                />
                <Button
                  type='button'
                  variant='ghost'
                  size='icon-sm'
                  aria-label={t`Do not include this document`}
                  title={t`Do not include this document`}
                  disabled={disabled}
                  onClick={() => onRemove(citation)}>
                  <X />
                </Button>
              </CitationChip>
            ))}
          </CitationList>
          <Text as='p' variant='caption' color='muted'>
            {t`Select a document to open it in the preview.`}
          </Text>
        </>
      ) : (
        <Text as='p' variant='caption' color='muted'>
          {t`No documents cited`}
        </Text>
      )}
      <AddPageRow>
        <Combobox value={documentId || undefined} onValueChange={pickDocument}>
          <ComboboxTrigger
            size='sm'
            aria-label={t`Document to cite`}
            title={documentId ? titleFor(documentId) : undefined}
            disabled={disabled}
            placeholder={t`Pick a document`}>
            {documentId ? titleFor(documentId) : undefined}
          </ComboboxTrigger>
          <ComboboxContent>
            <ComboboxInput
              placeholder={t`Paste a document id or link`}
              value={documentSearch}
              onValueChange={setDocumentSearch}
            />
            <ComboboxList>
              <ComboboxEmpty>
                {pasteMessage ?? t`Paste a document id or link.`}
              </ComboboxEmpty>
              <ComboboxGroup>
                {pickerIds.map((id) => (
                  <ComboboxItem
                    key={id}
                    value={id}
                    label={titleFor(id)}
                    keywords={
                      id === pastedDocumentId
                        ? [titleFor(id), documentSearch]
                        : [titleFor(id)]
                    }>
                    {titleFor(id)}
                  </ComboboxItem>
                ))}
              </ComboboxGroup>
            </ComboboxList>
          </ComboboxContent>
        </Combobox>
        <Input
          type='number'
          inputMode='numeric'
          min={1}
          max={numPages ?? undefined}
          aria-label={t`Page number`}
          placeholder={t`Page`}
          value={pageText}
          disabled={disabled || !documentId}
          aria-invalid={pageError ? true : undefined}
          onChange={(event) => setPageText(event.target.value)}
          onKeyDown={addPickedOnEnter}
        />
        <Button
          type='button'
          variant='secondary'
          size='sm'
          disabled={!canAddPicked}
          onClick={addPicked}>
          {pickedAdded ? <Check /> : <Plus />}
          {pickedAdded ? t`Cited` : t`Add`}
        </Button>
      </AddPageRow>
      {pageError ? (
        <Text as='p' variant='caption' color='destructive'>
          {pageError}
        </Text>
      ) : null}
      {resultPages.length ? (
        <>
          <Box display='flex'>
            <Button
              type='button'
              variant='ghost'
              size='sm'
              aria-expanded={resultsOpen}
              onClick={() => setResultsOpen((open) => !open)}>
              {t`Add from search results`}
              {resultsOpen ? (
                <ChevronUp aria-hidden data-icon='inline-end' />
              ) : (
                <ChevronDown aria-hidden data-icon='inline-end' />
              )}
            </Button>
          </Box>
          {resultsOpen ? (
            <ResultList>
              {resultPages.map((result) => {
                const title = titleFor(result.documentId)
                return (
                  <Item key={result.documentId} variant='outline' size='xs'>
                    <ItemContent>
                      <Text variant='caption' weight='medium' truncate>
                        {title}
                      </Text>
                      {result.pages.length ? (
                        <PageButtons>
                          {result.pages.map((pageNum) => {
                            const added = isAdded(result.documentId, pageNum)
                            return (
                              <Button
                                key={pageNum}
                                type='button'
                                variant='outline'
                                size='xs'
                                aria-label={
                                  added
                                    ? t`Page ${pageNum} of ${title} is cited`
                                    : t`Add page ${pageNum} of ${title}`
                                }
                                disabled={disabled || added}
                                onClick={() =>
                                  addPage(result.documentId, pageNum)
                                }>
                                {added ? <Check /> : <Plus />}
                                {t`p.${pageNum}`}
                              </Button>
                            )
                          })}
                        </PageButtons>
                      ) : (
                        <Text variant='caption' color='muted'>
                          {t`No matched pages. Paste its document id or link above and enter a page number.`}
                        </Text>
                      )}
                    </ItemContent>
                    {onPreviewDocument &&
                    citationLookup.has(result.documentId) ? (
                      <ItemActions variant='trailing'>
                        <Button
                          type='button'
                          variant='ghost'
                          size='xs'
                          onClick={() =>
                            openResult(result.documentId, result.pages[0] ?? 1)
                          }>
                          {t`Open`}
                        </Button>
                      </ItemActions>
                    ) : null}
                  </Item>
                )
              })}
            </ResultList>
          ) : null}
        </>
      ) : null}
    </Field>
  )
}
